import { PriceResponseV2 } from "@ssc-booking/api-contracts"
import * as A from "fp-ts/Array"
import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import * as ROA from "fp-ts/ReadonlyArray"
import { SanitySuite } from "../../../_common/sanity/sanity-types"
import { suiteCategoryPriceByCodes, SuitePriceE } from "./suite-prices"

export type SuiteCategory = { price: PriceResponseV2; content: SuiteContentWithConfiguration<SanitySuite> }

type SuiteContent = { configurations: { code: string }[] }
export type SuiteContentWithConfiguration<T extends SuiteContent> = Omit<T, "configurations"> & {
    configuration: T["configurations"][0]
}
export type SuiteWithPriceAndConfiguration<T extends SuiteContent> = {
    content: SuiteContentWithConfiguration<T>
    price: PriceResponseV2
}

export const suiteCategories = <T extends SuiteContent>(
    content: readonly T[],
    suitePrices: readonly SuitePriceE[],
): readonly SuiteWithPriceAndConfiguration<T>[] =>
    F.pipe(
        content,
        ROA.map((sanitySuite) => toSuiteWithPriceAndConfiguration(sanitySuite, suitePrices)),
        ROA.compact,
    )

export const maybeSuiteWithConfigurationByCode = <T extends SuiteContent>(
    suites: T[],
    code: string,
): O.Option<SuiteContentWithConfiguration<T>> =>
    F.pipe(
        suites,
        //
        A.findFirstMap((suite) => toSuiteWithConfiguration(suite, code)),
    )

const toSuiteWithConfiguration = <T extends SuiteContent>(
    suite: T,
    code: string,
): O.Option<SuiteContentWithConfiguration<T>> => {
    const { configurations, ...x } = suite
    const config = F.pipe(
        configurations,
        A.findFirst((x) => x.code == code),
    )

    if (O.isNone(config)) return O.none

    return O.some({
        ...x,
        configuration: config.value,
    })
}

const toSuiteWithPriceAndConfiguration = <T extends SuiteContent>(
    suiteContent: T,
    suitePrices: readonly SuitePriceE[],
): O.Option<SuiteWithPriceAndConfiguration<T>> => {
    const suiteContentCodes = suiteContent.configurations.map((c) => c.code)

    return F.pipe(
        suiteCategoryPriceByCodes(suitePrices, suiteContentCodes),
        O.chain((price) =>
            F.pipe(
                toSuiteWithConfiguration(suiteContent, price.suiteCategory),
                O.map((content) => ({
                    content,
                    price,
                })),
            ),
        ),
    )
}
